/**
 * EmailReportDialog — modal to send a branded PDF report via email.
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';

export default function EmailReportDialog({ open, onClose, onGeneratePdf, reportLabel }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim() || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!onGeneratePdf) { toast.error('PDF generation not available'); return; }
    setSending(true);
    try {
      const doc = onGeneratePdf();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const r = await api.post('/api/inventory/mgmt-reports/email-report', {
        report_id: 'module-report', email: email.trim(), filters: {},
        pdf_base64: pdfBase64, report_label: reportLabel || 'Report',
      });
      toast.success(r.data.message || 'Email sent');
      setEmail(''); onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to send email')); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="email-report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email Report</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">A branded PDF will be sent as an attachment.</p>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Recipient Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="colleague@company.com" className="h-9 text-sm" data-testid="email-report-input" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handleSend} disabled={sending} size="sm" data-testid="email-report-send"><Send className="h-3.5 w-3.5 mr-1.5" /> {sending ? 'Sending...' : 'Send Report'}</Button>
            <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
